<div class="container" *transloco="let t; prefix: 'dashboard-list'">
    <!-- Header avec titre et bouton de création -->
    <div class="mx-4 flex items-center justify-between">
        <app-page-title [title]="title" [breadcrumbs]="breadcrumbs" />
        <div class="-mb-3 mt-6 flex items-center gap-2 sm:mb-0 sm:mt-0">
            <!-- Filtres actifs (chips) -->
            @if (hasActiveFilters()) {
            <div class="flex flex-wrap items-center gap-1">
                @if (activeSubSector()) {
                <span class="filter-chip">
                    {{ t('sub-sector') }}: {{ activeSubSector()?.name }}
                    <mat-icon svgIcon="heroicons_mini:x-mark" class="chip-remove" (click)="clearSubSectorFilter()" />
                </span>
                }
                @if (activeThematic()) {
                <span class="filter-chip">
                    {{ t('thematic') }}: {{ activeThematic()?.name }}
                    <mat-icon svgIcon="heroicons_mini:x-mark" class="chip-remove" (click)="clearThematicFilter()" />
                </span>
                }
            </div>
            }

            <!-- Bouton dropdown filtre -->
            <button mat-icon-button [matMenuTriggerFor]="filterMenu" [color]="hasActiveFilters() ? 'primary' : 'basic'"
                [disabled]="subSectors().length <= 0 || (activeSubSector() !== null && thematics().length <= 0)"
                matTooltip="{{ t('filters') }}">
                <mat-icon svgIcon="filter_alt" />
            </button>

            <!-- Menu principal des filtres -->
            <mat-menu #filterMenu="matMenu">
                @if (!activeSubSector() && subSectors().length > 0) {
                <button mat-menu-item [matMenuTriggerFor]="subSectorMenu">
                    <mat-icon svgIcon="heroicons_outline:building-office-2" />
                    <span>{{ t('filter-by-sub-sector') }}</span>
                </button>
                }
                @if (activeSubSector() && thematics().length > 0) {
                <button mat-menu-item [matMenuTriggerFor]="thematicMenu">
                    <mat-icon svgIcon="heroicons_outline:light-bulb" />
                    <span>{{ t('filter-by-thematic') }}</span>
                </button>
                }
            </mat-menu>

            <!-- Sous-menu sous-secteur -->
            <mat-menu #subSectorMenu="matMenu">
                @for (subSector of subSectors(); track subSector.id) {
                <button mat-menu-item (click)="applySubSectorFilter(subSector)">
                    {{ subSector.name }}
                </button>
                }
            </mat-menu>

            <!-- Sous-menu thématique -->
            <mat-menu #thematicMenu="matMenu">
                @for (thematic of thematics(); track thematic.id) {
                <button mat-menu-item (click)="applyThematicFilter(thematic)">
                    {{ thematic.name }}
                </button>
                }
            </mat-menu>

            <!-- Bouton switch mode grid / group -->
            <button mat-icon-button color="accent" (click)="toggleMode()"
                [matTooltip]="mode() === 'grid' ? t('view-mode.group') : t('view-mode.grid')">
                <mat-icon
                    [svgIcon]="mode() === 'grid' ? 'heroicons_outline:squares-2x2' : 'heroicons_outline:rectangle-group'" />
            </button>

            <app-search-bar [formControl]="searchControl" [placeholder]="'dashboard-list.search-placeholder'"
                containerClass="mt-0" />
            @if (isAdmin()) {
            <button mat-raised-button color="primary" (click)="onCreateDashboard()">
                <mat-icon svgIcon="heroicons_outline:plus" />
                <!-- <span class="ml-2">{{ t('create-dashboard') }}</span> -->
            </button>
            }
        </div>
    </div>

    <!-- Navigateur des secteurs -->
    <div class="mx-auto mb-4 w-full">
        <nav mat-tab-nav-bar [tabPanel]="tabPanel" mat-stretch-tabs="false" mat-align-tabs="center">
            @for (item of sectors(); track item) {
            <a mat-tab-link [active]="item.id === selectedSectorId()" [routerLink]="['/data-management', 'dashboards']"
                [queryParams]="{ sectorId: item.id }">
                {{ item.name }}
            </a>
            }
        </nav>
    </div>

    <!-- Contenu principal -->
    <mat-tab-nav-panel #tabPanel>
        <div class="page-content">
            <div class="flex h-full flex-auto flex-col overflow-auto">
                <!-- Loading state -->
                @if (isLoading()) {
                <div class="flex h-64 items-center justify-center">
                    <mat-spinner diameter="40" />
                </div>
                }

                <!-- Empty state -->
                @if (!isLoading() && dashboardToDisplay().length === 0) {
                <div class="flex h-64 flex-col items-center justify-center">
                    <mat-icon svgIcon="heroicons_outline:presentation-chart-line" class="text-hint icon-size-24" />
                    <div class="text-secondary mt-4 text-2xl font-semibold tracking-tight">
                        {{ t('empty-state.title') }}
                    </div>
                    <div class="text-secondary mt-2">
                        {{ t('empty-state.description') }}
                    </div>
                    <button mat-raised-button color="primary" class="mt-6" (click)="onCreateDashboard()">
                        <mat-icon svgIcon="heroicons_outline:plus" />
                        <span class="ml-2">{{t('create-first-dashboard')}}</span>
                    </button>
                </div>
                }

                <!-- Grille des dashboards -->
                @if (!isLoading() && dashboardToDisplay().length > 0) {
                @if (mode() === 'grid') {
                <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    @for ( dashboard of dashboardToDisplay(); track dashboard.id ) {
                    <app-dashboard-card [dashboard]="dashboard" [isPreferred]="isPreferred(dashboard)"
                        [showActions]="true" [showPreferredButton]="true" [translationPrefix]="'dashboard-list'"
                        (click)="onViewDashboard(dashboard)" (view)="onViewDashboard($event)"
                        (editDesign)="onEditDesign($event)" (edit)="onEditDashboard($event)"
                        (delete)="onDeleteDashboard($event)" (share)="openShareDialog($event.id)"
                        (togglePreferred)="onTogglePreferred($event)" [@slideInRight] [@slideOutLeft] />
                    }
                </div>
                }
                @if (mode() === 'group') {
                <div class="gap-2">
                    @for (group of groupedDashboards(); track group) {
                    <div class="mb-4">
                        <mat-divider></mat-divider>
                        <h2 class="mt-4 mb-2 text-sm font-semibold tracking-tight">
                            {{ group.subSectorName }} / {{ group.thematicName }}
                        </h2>
                        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            @for ( dashboard of group.dashboards; track dashboard.id ) {
                            <app-dashboard-card [dashboard]="dashboard" [isPreferred]="isPreferred(dashboard)"
                                [showActions]="true" [showPreferredButton]="true" [translationPrefix]="'dashboard-list'"
                                (click)="onViewDashboard(dashboard)" (view)="onViewDashboard($event)"
                                [showThematic]="false" (editDesign)="onEditDesign($event)"
                                (edit)="onEditDashboard($event)" (delete)="onDeleteDashboard($event)"
                                (share)="openShareDialog($event.id)" (togglePreferred)="onTogglePreferred($event)"
                                [@slideInRight] [@slideOutLeft] />
                            }
                        </div>
                    </div>
                    }
                </div>
                }
                }
            </div>
        </div>
    </mat-tab-nav-panel>
</div>